import React, { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { Box, Button } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import messages from '@utils/messages'
import { useCurrentUser } from '@utils/useCurrentUser'
import { useResendEmailMutation } from '@src/mutations/ResendMailMutation.generated'
import {
  SnackbarMessages,
  useGlobalSnackbar,
} from '@src/components/GlobalSnackbar/globalSnackBarContext'
import Modal from '@src/components/Modal'

const useStyles = makeStyles((theme: Theme) => {
  return {
    paper: {
      borderRadius: 0,
    },
    dialogHeader: {
      fontSize: theme.typography.pxToRem(28),
      fontWeight: theme.typography.fontWeightBold,
      padding: theme.spacing(8, 8, 0, 8),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
    },
    closeIcon: {
      fontSize: theme.typography.pxToRem(30),
    },
    dialogBody: {
      padding: theme.spacing(4, 8),
      fontSize: theme.typography.pxToRem(16),
    },
    resendButton: {
      position: 'relative',
      marginLeft: 'auto',
      marginTop: theme.spacing(5),
      marginBottom: theme.spacing(6),
      fontSize: theme.typography.pxToRem(16),
      width: theme.spacing(36),
      height: theme.spacing(10),
    },
    userEmail: {
      fontWeight: theme.typography.fontWeightBold,
    },
    linkText: {
      cursor: 'pointer',
      margin: '0px',
      padding: '0px',
      color: theme.palette.primary.main,
    },
    btnLabel: {
      display: 'flex',
      justifyContent: 'flex-start',
      fontSize: theme.typography.pxToRem(16),
      marginTop: theme.spacing(-1),
    },
    emproveText: {
      fontSize: theme.typography.pxToRem(16),
    },
  }
})

interface EmailVerificationModalProps {
  open: boolean
  setModalOpen: (boolean) => void
  setEmailSent: (boolean) => void
  emailSent: boolean
}

const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({
  open,
  setModalOpen,
  emailSent,
  setEmailSent,
}) => {
  const classes = useStyles()
  const { currentUser } = useCurrentUser()
  const { setSnackbar } = useGlobalSnackbar()
  const [resendEmailMutation, { loading: sending }] = useResendEmailMutation()
  const userEmail =
    currentUser?.__typename === 'LoggedInUser' ? currentUser?.email : ''

  const resendEmail = async () => {
    try {
      await resendEmailMutation()
      setModalOpen(false)
      setSnackbar(SnackbarMessages.resendVerificationLink)
    } catch (e) {
      setSnackbar(SnackbarMessages.GenericError)
    }
  }

  useEffect(() => {
    // as per Kristien , we need to send email verification email when user open the modal
    // Update emailSent variable in parent class so we would not be sending muliple emails when user open this modal multiple times
    const sendEmailVerifiaction = async () => {
      try {
        await resendEmailMutation()
        setEmailSent(true)
        setSnackbar(SnackbarMessages.resendVerificationLink)
      } catch (e) {
        setSnackbar(SnackbarMessages.GenericError)
      }
    }
    if (open && !emailSent) {
      sendEmailVerifiaction()
    }
  }, [open, emailSent])

  return (
    <Modal
      open={open}
      setModalOpen={setModalOpen}
      headerContent={
        <FormattedMessage
          id="EMPROVE_EMAIL_VERIFICATION_HEADER"
          defaultMessage="Emprove® Program - Verify Your Identity"
        />
      }
      bodyContent={
        <>
          <Box mr={12}>
            <Box mb={5}>
              <div className={classes.emproveText}>
                <FormattedMessage
                  {...messages.EMPROVE_EMAIL_VERIFICATION_BODY_PT_1}
                />
              </div>
            </Box>
            <Box mb={5}>
              <div className={classes.emproveText}>
                <FormattedMessage
                  {...messages.EMPROVE_EMAIL_VERIFICATION_BODY_PT_2}
                />
              </div>
            </Box>
            <Box mb={5}>
              <div className={classes.emproveText}>
                <FormattedMessage
                  {...messages.EMPROVE_EMAIL_VERIFICATION_BODY_PT_3}
                  values={{
                    userEmail: (
                      <span className={classes.userEmail} data-scrub>
                        {userEmail}
                      </span>
                    ),
                    sendNewLink: (
                      <Button
                        disabled={sending}
                        onClick={resendEmail}
                        classes={{
                          root: classes.linkText,
                          label: classes.btnLabel,
                        }}
                      >
                        <FormattedMessage {...messages.SEND_NEW_LINK} />
                      </Button>
                    ),
                  }}
                />
              </div>
            </Box>
          </Box>
          <Button
            className={classes.resendButton}
            color="primary"
            variant="contained"
            onClick={() => setModalOpen(false)}
          >
            <FormattedMessage {...messages.OK} />
          </Button>
        </>
      }
    />
  )
}

export default EmailVerificationModal
